import { FileDropInput } from './file-drop-input.js';

const dropZone = document.getElementById('dropZone');
const fileInput = document.getElementById('imageInput');
const previewCanvas = document.getElementById('previewCanvas');
const statusEl = document.getElementById('status');
const convertStatusEl = document.getElementById('convertStatus');
const metaEl = document.getElementById('meta');
const formatSelect = document.getElementById('formatSelect');
const qualityInput = document.getElementById('qualityInput');
const qualityValue = document.getElementById('qualityValue');
const grayscaleInput = document.getElementById('grayscaleInput');
const resizeOutputInput = document.getElementById('resizeOutputInput');
const resizeModeSelect = document.getElementById('resizeModeSelect');
const resizeSizeInput = document.getElementById('resizeSizeInput');
const convertBtn = document.getElementById('convertBtn');
const autoDownloadInput = document.getElementById('autoDownloadInput');
const resultListEl = document.getElementById('resultList');

const ctx = previewCanvas.getContext('2d');
const MAX_PREVIEW_SIZE = 400;
const LOSSY_FORMATS = new Set(['image/jpeg', 'image/webp', 'image/avif']);
const EXTENSION_MAP = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/avif': 'avif'
};
const SVG_MIME_TYPES = new Set(['image/svg+xml']);
const HEIC_MIME_TOKENS = ['image/heic', 'image/heif'];
const SUPPORTED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'avif', 'tif', 'tiff', 'svg', 'heic', 'heif'];

const state = {
  queue: [],
  downloads: [],
  processing: false
};
const HEIC_DECODER_TIMEOUT_MS = 10000;

function setStatus(message) {
  statusEl.textContent = message;
}

function setConvertStatus(message) {
  convertStatusEl.textContent = message;
}

function resetPreview() {
  ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
  metaEl.textContent = '';
  convertBtn.disabled = state.queue.length === 0;
  setConvertStatus('Drop files to queue them.');
}

function revokeDownloadUrls() {
  state.downloads.forEach(({ url }) => URL.revokeObjectURL(url));
  state.downloads = [];
}

// function updatePreview(entry) {
//   const { canvas, size, type } = entry;
//   scalePreview(canvas);
//   updateMeta({ width: canvas.width, height: canvas.height, size, type });
// }

function updateMeta(info) {
  if (!info) {
    metaEl.textContent = '';
    return;
  }

  metaEl.textContent = [
    `Image: ${info.width} x ${info.height}`,
    `Size: ${formatBytes(info.size)}`,
    `Type: ${info.type || 'unknown'}`
  ].join(' | ');
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return 'n/a';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
}

function getFilenameTimestamp() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
}

function scalePreview(sourceCanvas) {
  const { width, height } = sourceCanvas;
  const scale = Math.min(1, MAX_PREVIEW_SIZE / Math.max(width, height));
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));
  previewCanvas.width = targetWidth;
  previewCanvas.height = targetHeight;
  ctx.clearRect(0, 0, targetWidth, targetHeight);
  ctx.drawImage(sourceCanvas, 0, 0, targetWidth, targetHeight);
}

async function handleIncomingFile(file) {
  if (!file) return;
  if (!isSupportedInputFile(file)) {
    setStatus('Please select an image file (png, jpg, webp, avif, tiff, svg, heic).');
    return;
  }

  setStatus(`Loading ${file.name}...`);

  try {
    const canvas = await loadFileToCanvas(file);
    const entry = {
      file,
      canvas,
      name: file.name,
      type: file.type,
      size: file.size
    };
    state.queue.push(entry);
    updatePreview(entry);
    if (!state.processing) {
      convertBtn.disabled = false;
    }
    setStatus(`Queued ${file.name}. ${state.queue.length} in queue.`);
    if (autoDownloadInput?.checked && !state.processing) {
      setConvertStatus('Auto converting queue...');
      processQueue();
    } else {
      setConvertStatus('Press convert to process the queue.');
    }
  } catch (error) {
    console.error(error);
    setStatus(error instanceof Error ? error.message : 'Unable to load that image.');
  }
}

async function loadFileToCanvas(file) {
  if (isSvgFile(file)) {
    return loadSvgToCanvas(file);
  }

  if (isTiffFile(file)) {
    if (typeof Tiff === 'undefined') {
      throw new Error('TIFF support is not available.');
    }
    const buffer = await file.arrayBuffer();
    const tiff = new Tiff({ buffer });
    const canvas = tiff.toCanvas(0);
    if (!canvas) {
      throw new Error('Could not parse TIFF file.');
    }
    return canvas;
  }

  if (isHeicFile(file)) {
    return loadHeicToCanvas(file);
  }

  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext('2d');
  context.drawImage(bitmap, 0, 0);
  bitmap.close();
  return canvas;
}

function isSupportedInputFile(file) {
  if (!file) {
    return false;
  }

  return (
    (typeof file.type === 'string' && file.type.startsWith('image/')) ||
    hasSupportedExtension(file.name)
  );
}

function hasSupportedExtension(name) {
  const match = typeof name === 'string' ? name.toLowerCase().match(/\.([^.]+)$/) : null;
  return !!match && SUPPORTED_EXTENSIONS.includes(match[1]);
}

function isSvgFile(file) {
  const type = file?.type?.toLowerCase?.() || '';
  return SVG_MIME_TYPES.has(type) || /\.svg$/i.test(file?.name || '');
}

function isTiffFile(file) {
  const type = file?.type?.toLowerCase?.() || '';
  return type === 'image/tiff' || /\.tiff?$/i.test(file?.name || '');
}

function isHeicFile(file) {
  const type = file?.type?.toLowerCase?.() || '';
  return HEIC_MIME_TOKENS.some((token) => type.includes(token)) || /\.hei[cf]$/i.test(file?.name || '');
}

async function loadSvgToCanvas(file) {
  const svgText = await file.text();
  const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const image = await loadImage(svgUrl);
    const width = Math.max(1, image.naturalWidth || image.width || 1);
    const height = Math.max(1, image.naturalHeight || image.height || 1);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, width, height);
    return canvas;
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

async function loadHeicToCanvas(file) {
  await waitForHeicDecoder();

  const isHeic = typeof window.HeicTo.isHeic === 'function'
    ? await window.HeicTo.isHeic(file)
    : true;

  if (!isHeic) {
    throw new Error('This file does not look like a valid HEIC image.');
  }

  let decodedBlob = null;
  for (const mimeType of ['image/png', 'image/jpeg']) {
    try {
      decodedBlob = await window.HeicTo({
        blob: file,
        type: mimeType,
        quality: 0.92
      });
      if (decodedBlob) {
        break;
      }
    } catch (error) {
      decodedBlob = null;
    }
  }

  if (!decodedBlob) {
    throw new Error('Could not decode the HEIC image.');
  }

  const bitmap = await createImageBitmap(decodedBlob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext('2d');
  context.drawImage(bitmap, 0, 0);
  bitmap.close();
  return canvas;
}

async function waitForHeicDecoder() {
  const startTime = Date.now();

  while (Date.now() - startTime < HEIC_DECODER_TIMEOUT_MS) {
    if (window.__heicDecoderFailed) {
      throw new Error('The HEIC decoder script failed to load. Check internet access or host a local copy.');
    }

    if (window.__heicDecoderReady && window.HeicTo) {
      return;
    }

    await delay(100);
  }

  throw new Error('The HEIC decoder did not become available in time.');
}

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Invalid SVG image.'));
    image.src = src;
  });
}

function updatePreview(entry) {
  const { canvas, size, type, name } = entry;
  scalePreview(canvas);
  updateMeta({ width: canvas.width, height: canvas.height, size, type });
  setConvertStatus(`Latest: ${name} (${canvas.width}x${canvas.height})`);
}

function getOutputFilename(originalName, mimeType) {
  const ext = EXTENSION_MAP[mimeType] || 'png';
  if (!originalName) {
    return `converted.${ext}`;
  }

  let basename = originalName.replace(/\.[^.]+$/g, '');
  if (!basename) {
    basename = 'image';
  }
  if (/^image/i.test(basename)) {
    basename = `${getFilenameTimestamp()}_${basename}`;
  }
  return `${basename}.${ext}`;
}

function cloneCanvas(canvas) {
  const copy = document.createElement('canvas');
  copy.width = canvas.width;
  copy.height = canvas.height;
  copy.getContext('2d').drawImage(canvas, 0, 0);
  return copy;
}

function flattenCanvasBackground(canvas, fillStyle = '#ffffff') {
  const flattened = document.createElement('canvas');
  flattened.width = canvas.width;
  flattened.height = canvas.height;
  const context = flattened.getContext('2d');
  context.fillStyle = fillStyle;
  context.fillRect(0, 0, flattened.width, flattened.height);
  context.drawImage(canvas, 0, 0);
  return flattened;
}

function resizeCanvasTo(canvas, targetSize, mode) {
  if (!targetSize || targetSize <= 0) {
    return canvas;
  }
  const sourceWidth = canvas.width;
  const sourceHeight = canvas.height;
  if (mode === 'width') {
    if (sourceWidth === targetSize) return canvas;
    const scale = targetSize / sourceWidth;
    return drawResizedCanvas(canvas, targetSize, Math.max(1, Math.round(sourceHeight * scale)));
  }
  if (sourceHeight === targetSize) return canvas;
  const scale = targetSize / sourceHeight;
  return drawResizedCanvas(canvas, Math.max(1, Math.round(sourceWidth * scale)), targetSize);
}

function drawResizedCanvas(sourceCanvas, width, height) {
  const resized = document.createElement('canvas');
  resized.width = Math.max(1, Math.round(width));
  resized.height = Math.max(1, Math.round(height));
  resized.getContext('2d').drawImage(sourceCanvas, 0, 0, resized.width, resized.height);
  return resized;
}

function applyGrayscale(canvas) {
  const canvasCtx = canvas.getContext('2d');
  const imageData = canvasCtx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    data[i] = data[i + 1] = data[i + 2] = luminance;
  }
  canvasCtx.putImageData(imageData, 0, 0);
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to encode image.'));
        return;
      }
      resolve(blob);
    }, mimeType, quality);
  });
}

async function processQueue() {
  if (!state.queue.length || state.processing) {
    return;
  }

  state.processing = true;
  convertBtn.disabled = true;
  const outputFormat = formatSelect.value;
  const lossyQuality = Number(qualityInput.value) / 100;
  const quality = LOSSY_FORMATS.has(outputFormat) ? lossyQuality : undefined;

  if (!resultListEl.querySelector('.conversion-result')) {
    resultListEl.innerHTML = '';
  }

  setConvertStatus(`Converting ${state.queue.length} file(s)...`);

  while (state.queue.length) {
    const entry = state.queue.shift();
    try {
      let workingCanvas = cloneCanvas(entry.canvas);
      if (resizeOutputInput?.checked) {
        const targetSize = Number(resizeSizeInput.value);
        const mode = resizeModeSelect?.value === 'height' ? 'height' : 'width';
        if (Number.isFinite(targetSize) && targetSize > 0) {
          workingCanvas = resizeCanvasTo(workingCanvas, targetSize, mode);
        }
      }
      if (grayscaleInput.checked) {
        applyGrayscale(workingCanvas);
      }
      if (outputFormat === 'image/jpeg') {
        workingCanvas = flattenCanvasBackground(workingCanvas, '#ffffff');
      }
      const blob = await canvasToBlob(workingCanvas, outputFormat, quality);
      const filename = getOutputFilename(entry.name, outputFormat);
      const url = URL.createObjectURL(blob);
      state.downloads.push({ url });
      if (!autoDownloadInput || autoDownloadInput.checked) {
        triggerDownload(url, filename);
      }
      addResultEntry(filename, url, blob.size);
      setStatus(`Converted ${entry.name} → ${filename}`);
      updatePreview(entry);
    } catch (error) {
      console.error(error);
      setStatus(`Failed to convert ${entry.name}.`);
    }
  }

  setConvertStatus('All queued files processed.');
  state.processing = false;
  convertBtn.disabled = state.queue.length === 0;
  if (!state.queue.length) {
    setStatus('Queue empty. Drop more files to convert.');
  }
}

function triggerDownload(url, filename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function addResultEntry(filename, url, size) {
  const sizeLabel = formatBytes(size);
  if (!resultListEl.querySelector('.conversion-result')) {
    resultListEl.innerHTML = '';
  }

  const entry = document.createElement('div');
  entry.className = 'conversion-result';

  const info = document.createElement('div');
  info.className = 'conversion-result__info';
  info.innerHTML = `<strong>${filename}</strong><small>${sizeLabel}</small>`;

  const button = document.createElement('a');
  button.href = url;
  button.download = filename;
  button.className = 'conversion-result__download';
  button.textContent = 'Download again';

  entry.appendChild(info);
  entry.appendChild(button);
  resultListEl.appendChild(entry);
}

async function handleUrlInput(text) {
  if (!text) return;
  try {
    const url = new URL(text.trim());
    setStatus('Fetching image...');
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error('Network error.');
    }
    const blob = await response.blob();
    const file = new File([blob], url.pathname.split('/').pop() || 'image', { type: blob.type });
    await handleIncomingFile(file);
  } catch (error) {
    console.error(error);
    setStatus('Paste a direct image URL (https://example.com/file.png).');
  }
}

function handlePaste(event) {
  if (event.defaultPrevented) return;
  if (event.clipboardData?.files?.length) {
    return;
  }
  const text = event.clipboardData?.getData('text');
  handleUrlInput(text);
}

qualityInput.addEventListener('input', (event) => {
  const value = Number(event.target.value) || 0;
  qualityValue.textContent = `${value}%`;
});

convertBtn.addEventListener('click', processQueue);
document.addEventListener('paste', handlePaste);

if (dropZone && fileInput) {
  new FileDropInput({
    dropZone,
    input: fileInput,
    dropTarget: document.body,
    onFile: handleIncomingFile,
    pasteTarget: document,
    validateFile: isSupportedInputFile,
    listenForPaste: true
  });
}

resetPreview();
setStatus('Drop or paste images to queue them.');
setConvertStatus('Drop files to begin.');
window.addEventListener('beforeunload', revokeDownloadUrls);
